/**
 * RatioBar — horizontal pass/fail ratio bar with numeric trailing label.
 * Tone is derived from pct: ≥95 pass-green, ≥90 warn-amber, < 90 fail-red.
 * Matches the design/ui RatioBar used in tenant + domain tables.
 */
import { cn } from './cn';

export interface RatioBarProps {
  /** Percentage 0-100, or null/undefined for unknown ("—"). */
  pct: number | null | undefined;
  /** Bar width in pixels; defaults to 80. */
  width?: number;
  className?: string;
}

function formatPct(pct: number): string {
  return `${pct.toFixed(1).replace('.', ',')} %`;
}

export function RatioBar({ pct, width = 80, className }: RatioBarProps): JSX.Element {
  if (pct == null) {
    return <span className="text-xs text-fg-disabled">—</span>;
  }
  const clamped = Math.min(Math.max(pct, 0), 100);
  const toneClass = clamped >= 95 ? 'bg-pass-600' : clamped >= 90 ? 'bg-warn-600' : 'bg-fail-600';
  return (
    <div className={cn('flex items-center gap-2', className)}>
      <div
        className="h-1.5 overflow-hidden rounded bg-slate-100"
        style={{ width }}
        role="img"
        aria-label={`Pass-ratio ${formatPct(clamped)}`}
      >
        <div className={cn('h-full', toneClass)} style={{ width: `${clamped}%` }} />
      </div>
      <span className="font-mono text-xs tabular-nums text-fg-secondary">{formatPct(clamped)}</span>
    </div>
  );
}
